import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Server, Database, Wifi, Cloud, Zap, Clock, ArrowLeft,
  ChevronDown, ChevronRight, Loader2, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  healthy: { color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: CheckCircle, label: 'Healthy' },
  pass: { color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: CheckCircle, label: 'Pass' },
  unhealthy: { color: 'bg-red-500', textColor: 'text-red-400', icon: XCircle, label: 'Unhealthy' },
  fail: { color: 'bg-red-500', textColor: 'text-red-400', icon: XCircle, label: 'Fail' },
  error: { color: 'bg-red-500', textColor: 'text-red-400', icon: XCircle, label: 'Error' },
  unavailable: { color: 'bg-red-500', textColor: 'text-red-400', icon: XCircle, label: 'Unavailable' },
  timeout: { color: 'bg-amber-500', textColor: 'text-amber-400', icon: AlertTriangle, label: 'Timeout' },
  warn: { color: 'bg-amber-500', textColor: 'text-amber-400', icon: AlertTriangle, label: 'Warning' },
  not_configured: { color: 'bg-slate-500', textColor: 'text-slate-400', icon: Info, label: 'Not Configured' },
  unknown: { color: 'bg-slate-500', textColor: 'text-slate-400', icon: Info, label: 'Unknown' },
  ready: { color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: CheckCircle, label: 'Ready' },
  not_ready: { color: 'bg-red-500', textColor: 'text-red-400', icon: XCircle, label: 'Not Ready' },
};

const serviceIcons = {
  'GRC Backend': Server,
  'OSINT Backend': Cloud,
  'MongoDB': Database,
  'Redis': Zap,
  'WebSocket': Wifi,
};

const HealthDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [preflightData, setPreflightData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedChecks, setExpandedChecks] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHealth = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [healthRes, preflightRes] = await Promise.all([
        fetch(`${API_URL}/api/system/health`, { headers }),
        fetch(`${API_URL}/api/system/health/preflight`, { headers })
      ]);

      if (!healthRes.ok) throw new Error(`Health check failed: ${healthRes.status}`);
      if (!preflightRes.ok) throw new Error(`Preflight check failed: ${preflightRes.status}`);

      const healthJson = await healthRes.json();
      const preflightJson = await preflightRes.json();

      setHealthData(healthJson);
      setPreflightData(preflightJson);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Health check error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(() => fetchHealth(true), 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const toggleExpand = (checkName) => {
    setExpandedChecks(prev => ({ ...prev, [checkName]: !prev[checkName] }));
  };

  const getStatusConfig = (status) => statusConfig[status] || statusConfig.unknown;

  const StatusIcon = ({ status, className = "w-5 h-5" }) => {
    const config = getStatusConfig(status);
    const Icon = config.icon;
    return <Icon className={`${className} ${config.textColor}`} />;
  };

  const StatusBadge = ({ status }) => {
    const config = getStatusConfig(status);
    return (
      <Badge className={`${config.color} text-white border-0`}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="health-dashboard-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Running system health checks...</p>
        </div>
      </div>
    );
  }

  const overallStatus = healthData?.status || 'unknown';
  const preflightStatus = preflightData?.status || 'unknown';

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6" data-testid="health-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/grc/dashboard')}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Activity className="w-7 h-7 text-cyan-400" />
              System Health Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Monitor the status of all platform components
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <span className="text-slate-500 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button
            onClick={() => fetchHealth(true)}
            disabled={refreshing}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="refresh-health"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6" data-testid="health-error">
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        </div>
      )}

      {/* Overall Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* System Status */}
        <Card className="bg-slate-900/50 border-slate-800" data-testid="system-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-300">System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full ${getStatusConfig(overallStatus).color} flex items-center justify-center`}>
                  <StatusIcon status={overallStatus} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold capitalize">{overallStatus}</p>
                  <p className="text-slate-400 text-sm">
                    {healthData?.summary?.healthy || 0} of {healthData?.summary?.total || 0} services healthy
                  </p>
                </div>
              </div>
              {healthData?.summary && (
                <div className="text-right">
                  <div className="text-sm text-slate-400">
                    <span className="text-emerald-400">{healthData.summary.healthy} healthy</span>
                    {healthData.summary.unhealthy > 0 && (
                      <span className="text-red-400 ml-2">{healthData.summary.unhealthy} unhealthy</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preflight Status */}
        <Card className="bg-slate-900/50 border-slate-800" data-testid="preflight-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-300">Preflight Check</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full ${getStatusConfig(preflightStatus).color} flex items-center justify-center`}>
                  <StatusIcon status={preflightStatus} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold capitalize">{preflightStatus.replace('_', ' ')}</p>
                  <p className="text-slate-400 text-sm">
                    {preflightData?.summary?.passed || 0} of {preflightData?.summary?.total_checks || 0} checks passed
                  </p>
                </div>
              </div>
              {preflightData?.summary && (
                <div className="text-right text-sm text-slate-400">
                  {preflightData.summary.failed > 0 && (
                    <span className="text-red-400">{preflightData.summary.failed} failed</span>
                  )}
                  {preflightData.summary.warnings > 0 && (
                    <span className="text-amber-400 ml-2">{preflightData.summary.warnings} warnings</span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-300 mb-4">Service Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {healthData?.services?.map((service, idx) => {
            const IconComponent = serviceIcons[service.name] || Server;
            const config = getStatusConfig(service.status);
            return (
              <Card 
                key={idx} 
                className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors"
                data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${config.color}/20`}>
                      <IconComponent className={`w-5 h-5 ${config.textColor}`} />
                    </div>
                    <StatusBadge status={service.status} />
                  </div>
                  <h3 className="font-medium text-white mb-1">{service.name}</h3>
                  {service.latency_ms && (
                    <p className="text-xs text-slate-500">
                      Latency: {service.latency_ms}ms
                    </p>
                  )}
                  {service.error && (
                    <p className="text-xs text-red-400 mt-1 truncate" title={service.error}>
                      {service.error}
                    </p>
                  )}
                  {service.details && (
                    <div className="mt-2 pt-2 border-t border-slate-800">
                      {Object.entries(service.details).slice(0, 3).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="text-slate-300">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Preflight Checks */}
      <div>
        <h2 className="text-lg font-semibold text-slate-300 mb-4">Preflight Checklist</h2>
        <div className="space-y-3">
          {preflightData?.checks?.map((check, idx) => {
            const isExpanded = expandedChecks[check.name];
            const allPassed = check.items?.every(i => i.status === 'pass');
            const hasWarnings = check.items?.some(i => i.status === 'warn');
            const hasFailed = check.items?.some(i => i.status === 'fail');
            
            let checkStatus = 'pass';
            if (hasFailed) checkStatus = 'fail';
            else if (hasWarnings) checkStatus = 'warn';

            return (
              <Card 
                key={idx} 
                className="bg-slate-900/50 border-slate-800"
                data-testid={`preflight-check-${check.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
                  onClick={() => toggleExpand(check.name)}
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon status={checkStatus} />
                    <div>
                      <h3 className="font-medium text-white">{check.name}</h3>
                      <p className="text-xs text-slate-500 capitalize">{check.category}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-400">
                      {check.items?.filter(i => i.status === 'pass').length}/{check.items?.length} passed
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-800">
                    <div className="mt-3 space-y-2">
                      {check.items?.map((item, itemIdx) => (
                        <div 
                          key={itemIdx}
                          className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <StatusIcon status={item.status} className="w-4 h-4" />
                            <span className="text-sm text-slate-300">{item.name}</span>
                          </div>
                          <span className={`text-xs ${getStatusConfig(item.status).textColor}`}>
                            {item.message}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-slate-600 text-sm">
        <p>Auto-refreshes every 30 seconds</p>
      </div>
    </div>
  );
};

export default HealthDashboard;
